import React, {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DownloadIcon from '@mui/icons-material/Download';
import GroupsIcon from '@mui/icons-material/Groups';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { pageColors } from './HomePage';
import apiFetch from '../api/apiFetch';

const MIN_WEEKS = 2;
const MAX_WEEKS = 4;
const DEFAULT_WEEKS = 4;
const CUSTOMER_DETAIL_BACK_STATE = {
  backTo: '/customer-sales-trends',
  backLabel: 'Back to Customer Sales',
};
let customerSalesTrendsPageStateCache = null;

const CUSTOMER_SALES_TRENDS_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.customerSales,
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
});

const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(194,24,91,0.16), transparent 55%), #f5f7fb';

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const FILTER_SUMMARY_ITEM_SX = {
  minWidth: 0,
  px: 1,
  py: 0.75,
  borderRadius: 2,
  border: '1px solid rgba(0,0,0,0.08)',
  backgroundColor: 'rgba(194,24,91,0.05)',
};

const TABLE_HEADER_SX = {
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap',
  fontWeight: 700,
};

const TABLE_ROW_HOVER_SX = {
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
};

function formatSalesValue(value) {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return Number.isInteger(numericValue)
    ? numericValue.toLocaleString()
    : numericValue.toFixed(2);
}

function getSalesDifferenceMeta(indicator) {
  switch (indicator) {
    case -3:
      return {
        text: 'Decreasing trend bigger than 30%',
        shortLabel: 'Down > 30%',
        color: '#c62828',
        backgroundColor: 'rgba(211, 47, 47, 0.12)',
      };
    case -2:
      return {
        text: 'Decreasing trend between 10% and 30%',
        shortLabel: 'Down 10-30%',
        color: '#ef6c00',
        backgroundColor: 'rgba(245, 124, 0, 0.14)',
      };
    case -1:
      return {
        text: 'Decreasing trend lower than 10%',
        shortLabel: 'Down < 10%',
        color: '#8d6e00',
        backgroundColor: 'rgba(251, 192, 45, 0.18)',
      };
    case 1:
      return {
        text: 'Increasing trend lower than 10%',
        shortLabel: 'Up < 10%',
        color: '#2e7d32',
        backgroundColor: 'rgba(56, 142, 60, 0.12)',
      };
    case 2:
      return {
        text: 'Increasing trend between 10% and 30%',
        shortLabel: 'Up 10-30%',
        color: '#2e7d32',
        backgroundColor: 'rgba(46, 125, 50, 0.14)',
      };
    case 3:
      return {
        text: 'Increasing trend bigger than 30%',
        shortLabel: 'Up > 30%',
        color: '#1b5e20',
        backgroundColor: 'rgba(46, 125, 50, 0.18)',
      };
    default:
      return {
        text: 'No Change',
        shortLabel: 'No Change',
        color: '#616161',
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
      };
  }
}

function buildTrendRow(trend, index) {
  const customerNumber = String(trend?.customerNumber ?? '');
  const customerName = String(trend?.customerName ?? '');
  const averageWeeklySales = Number(trend?.averageWeeklySales) || 0;
  const lastWeekSales = Number(trend?.lastWeekSales) || 0;
  const salesDifferenceIndicator = Number(trend?.salesDifferenceIndicator) || 0;
  const salesDifferenceMeta = getSalesDifferenceMeta(salesDifferenceIndicator);

  return {
    key: customerNumber || `${customerName}-${index}`,
    customerNumber,
    customerNumberDisplay: customerNumber || 'N/A',
    customerName,
    customerNameDisplay: customerName || 'N/A',
    customerNumberSearch: customerNumber.toLowerCase(),
    customerNameSearch: customerName.toLowerCase(),
    averageWeeklySales,
    lastWeekSales,
    salesDifferenceIndicator,
    salesDifferenceMeta,
    formattedAverageWeeklySales: formatSalesValue(averageWeeklySales),
    formattedLastWeekSales: formatSalesValue(lastWeekSales),
  };
}

function renderDifferencePill(meta) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 88,
        px: 1.25,
        py: 0.5,
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.2,
        color: meta.color,
        backgroundColor: meta.backgroundColor,
        border: '1px solid rgba(0,0,0,0.05)',
      }}
    >
      {meta.shortLabel}
    </Box>
  );
}

const CustomerSalesTrendsResultsTable = memo(function CustomerSalesTrendsResultsTable({
  rows,
  order,
  orderBy,
  onRequestSort,
  hasLoadedData,
}) {
  const renderedRows = useMemo(() => {
    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
            {hasLoadedData
              ? 'No customers match the current filter.'
              : 'No customer sales trends data found. Click "Load Data" to fetch data.'}
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((row) => (
      <TableRow key={row.key} hover sx={TABLE_ROW_HOVER_SX}>
        <TableCell>{row.customerNumberDisplay}</TableCell>
        <TableCell>{row.customerNameDisplay}</TableCell>
        <TableCell>{row.formattedAverageWeeklySales}</TableCell>
        <TableCell>{row.formattedLastWeekSales}</TableCell>
        <TableCell>{renderDifferencePill(row.salesDifferenceMeta)}</TableCell>
        <TableCell>
          {row.customerNumber ? (
            <Button
              component={Link}
              to={`/customers/${encodeURIComponent(row.customerNumber)}`}
              state={CUSTOMER_DETAIL_BACK_STATE}
              size="small"
              variant="text"
              endIcon={<ArrowForwardRoundedIcon fontSize="small" />}
              sx={{ textTransform: 'none' }}
            >
              Open
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          )}
        </TableCell>
      </TableRow>
    ));
  }, [hasLoadedData, rows]);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...SURFACE_SX,
        maxHeight: '72vh',
        overflowY: 'auto',
      }}
    >
      <Table
        stickyHeader
        sx={{ tableLayout: 'fixed', width: '100%' }}
        aria-label="customer sales trends table"
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '14%' }}>
              <TableSortLabel
                active={orderBy === 'customerNumber'}
                direction={orderBy === 'customerNumber' ? order : 'asc'}
                onClick={() => onRequestSort('customerNumber')}
              >
                Customer #
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '24%' }}>
              <TableSortLabel
                active={orderBy === 'customerName'}
                direction={orderBy === 'customerName' ? order : 'asc'}
                onClick={() => onRequestSort('customerName')}
              >
                Customer Name
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '16%' }}>
              <TableSortLabel
                active={orderBy === 'averageWeeklySales'}
                direction={orderBy === 'averageWeeklySales' ? order : 'asc'}
                onClick={() => onRequestSort('averageWeeklySales')}
              >
                Avg Weekly Sales
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '16%' }}>
              <TableSortLabel
                active={orderBy === 'lastWeekSales'}
                direction={orderBy === 'lastWeekSales' ? order : 'asc'}
                onClick={() => onRequestSort('lastWeekSales')}
              >
                Last Week Sales
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '16%' }}>
              <TableSortLabel
                active={orderBy === 'salesDifferenceIndicator'}
                direction={orderBy === 'salesDifferenceIndicator' ? order : 'asc'}
                onClick={() => onRequestSort('salesDifferenceIndicator')}
              >
                Sales Difference
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...TABLE_HEADER_SX, width: '14%' }}>
              Open
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderedRows}</TableBody>
      </Table>
    </TableContainer>
  );
});

export default function CustomerSalesTrendsPage({ connected, setConnected, setError }) {
  const [weeks, setWeeks] = useState(() => customerSalesTrendsPageStateCache?.weeks ?? DEFAULT_WEEKS);
  const [trends, setTrends] = useState(() => customerSalesTrendsPageStateCache?.trends ?? []);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(() => customerSalesTrendsPageStateCache?.order ?? 'asc');
  const [orderBy, setOrderBy] = useState(
    () => customerSalesTrendsPageStateCache?.orderBy ?? 'customerNumber'
  );
  const [customerNameFilter, setCustomerNameFilter] = useState(
    () => customerSalesTrendsPageStateCache?.customerNameFilter ?? ''
  );
  const deferredCustomerNameFilter = useDeferredValue(customerNameFilter);

  useEffect(() => {
    customerSalesTrendsPageStateCache = {
      weeks,
      trends,
      customerNameFilter,
      order,
      orderBy,
    };
  }, [customerNameFilter, order, orderBy, trends, weeks]);

  const handleSearch = async () => {
    if (!connected) {
      setError('Not connected to Fortnox. Please connect first.');
      setConnected(false);
      return;
    }

    if (weeks < MIN_WEEKS || weeks > MAX_WEEKS) {
      setError(`Please select a number of weeks between ${MIN_WEEKS} and ${MAX_WEEKS}.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/customer-sales-trends?weeks=${weeks}`);

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg;

        if (response.status === 401) {
          errMsg = 'Failed to connect to Fortnox: Unauthorized. Please try again.';
          setConnected(false);
        } else if (response.status === 503) {
          errMsg = 'Fortnox cannot handle requests at the moment. Please wait.';
          setConnected(true);
        } else {
          errMsg = `Failed to fetch customer sales trends: ${errorText || response.status}. Please try again.`;
          setConnected(false);
        }

        setError(errMsg);
        return;
      }

      const data = await response.json();
      const nextTrends = Array.isArray(data) ? data : [];

      setTrends(nextTrends);
      setConnected(true);
    } catch (err) {
      setError(`Error starting customer sales trends task: ${err.message}`);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const normalizedCustomerNameFilter = deferredCustomerNameFilter.trim().toLowerCase();

  const trendRows = useMemo(
    () =>
      Array.isArray(trends)
        ? trends
            .filter((trend) => trend !== null && trend !== undefined)
            .map((trend, index) => buildTrendRow(trend, index))
        : [],
    [trends]
  );

  const filteredRows = useMemo(() => {
    if (!normalizedCustomerNameFilter) {
      return trendRows;
    }

    return trendRows.filter((row) =>
      row.customerNameSearch.includes(normalizedCustomerNameFilter)
    );
  }, [trendRows, normalizedCustomerNameFilter]);

  const sortedRows = useMemo(() => {
    const comparator = (rowA, rowB) => {
      switch (orderBy) {
        case 'customerNumber':
          return order === 'asc'
            ? rowA.customerNumberSearch.localeCompare(rowB.customerNumberSearch)
            : rowB.customerNumberSearch.localeCompare(rowA.customerNumberSearch);
        case 'customerName':
          return order === 'asc'
            ? rowA.customerNameSearch.localeCompare(rowB.customerNameSearch)
            : rowB.customerNameSearch.localeCompare(rowA.customerNameSearch);
        case 'averageWeeklySales':
          return order === 'asc'
            ? rowA.averageWeeklySales - rowB.averageWeeklySales
            : rowB.averageWeeklySales - rowA.averageWeeklySales;
        case 'lastWeekSales':
          return order === 'asc'
            ? rowA.lastWeekSales - rowB.lastWeekSales
            : rowB.lastWeekSales - rowA.lastWeekSales;
        case 'salesDifferenceIndicator':
          return order === 'asc'
            ? rowA.salesDifferenceIndicator - rowB.salesDifferenceIndicator
            : rowB.salesDifferenceIndicator - rowA.salesDifferenceIndicator;
        default:
          return 0;
      }
    };

    return [...filteredRows].sort(comparator);
  }, [filteredRows, order, orderBy]);

  const displayWeeks =
    weeks >= MIN_WEEKS && weeks <= MAX_WEEKS ? Number(weeks) || DEFAULT_WEEKS : DEFAULT_WEEKS;
  const today = dayjs();
  const lastWeekStart = today.subtract(7, 'day').format('YYYY-MM-DD');
  const lastWeekEnd = today.subtract(1, 'day').format('YYYY-MM-DD');
  const averageStart = today.subtract(displayWeeks * 7, 'day').format('YYYY-MM-DD');
  const averageEnd = today.subtract(1, 'day').format('YYYY-MM-DD');
  const hasLoadedData = trendRows.length > 0;
  const hasFilteredResults = sortedRows.length > 0;
  const isApplyingFilters = customerNameFilter !== deferredCustomerNameFilter;

  const handleExportToExcel = () => {
    if (!hasFilteredResults) {
      setError('No data to export');
      return;
    }

    try {
      const exportData = sortedRows.map((row) => ({
        'Customer #': row.customerNumber,
        'Customer Name': row.customerName,
        'Avg Weekly Sales': row.averageWeeklySales,
        'Last Week Sales': row.lastWeekSales,
        'Sales Difference': row.salesDifferenceMeta.text,
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      worksheet['!cols'] = [
        { wch: 12 },
        { wch: 25 },
        { wch: 18 },
        { wch: 16 },
        { wch: 35 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Sales Trends');
      XLSX.writeFile(workbook, `customer_sales_trends_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export data to Excel');
    }
  };

  return (
    <ThemeProvider theme={CUSTOMER_SALES_TRENDS_THEME}>
      <Box
        sx={{
          minHeight: '100vh',
          background: PAGE_BACKGROUND,
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2.25}>
            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'rgba(194,24,91,0.14)',
                      color: pageColors.customerSales,
                    }}
                  >
                    <GroupsIcon fontSize="large" />
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="overline"
                      sx={{ color: pageColors.customerSales, letterSpacing: 3 }}
                    >
                      CUSTOMER SALES
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                      Customer Sales Trends
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                      Review which customers are buying more or less than usual so you can react
                      faster to shifting demand.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 } }}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Filters
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Narrow the customer list, refresh the current period, and export the filtered
                    result.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg: 'minmax(0, 1fr) auto',
                    },
                    gap: 1.25,
                    alignItems: 'start',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.25,
                      alignItems: 'start',
                    }}
                  >
                    <TextField
                      label="Customer Name"
                      value={customerNameFilter}
                      onChange={(event) => setCustomerNameFilter(event.target.value)}
                      size="small"
                      fullWidth
                      sx={{
                        width: { xs: '100%', sm: 320 },
                        flex: { sm: '0 0 auto' },
                      }}
                    />
                    <TextField
                      label="Weeks"
                      type="number"
                      value={weeks}
                      onChange={(event) => setWeeks(Number(event.target.value))}
                      size="small"
                      inputProps={{ min: MIN_WEEKS, max: MAX_WEEKS }}
                      helperText={`${MIN_WEEKS}-${MAX_WEEKS} weeks`}
                      fullWidth
                      sx={{
                        width: { xs: '100%', sm: 110 },
                        flex: { sm: '0 0 auto' },
                      }}
                    />
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        flex: { sm: '0 0 auto' },
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        disabled={loading}
                        sx={{ minHeight: 38, minWidth: { sm: 136 } }}
                      >
                        {loading ? 'Loading...' : 'Load Data'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportToExcel}
                        disabled={loading || !hasFilteredResults}
                        sx={{ minHeight: 38, minWidth: { sm: 136 } }}
                      >
                        Export
                      </Button>
                    </Stack>
                  </Box>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{
                      width: { xs: '100%', lg: 'auto' },
                      justifySelf: { lg: 'end' },
                    }}
                  >
                    <Box
                      sx={{
                        ...FILTER_SUMMARY_ITEM_SX,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 190 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: pageColors.customerSales,
                        }}
                      >
                        Last Week Sales
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {lastWeekStart} to {lastWeekEnd}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        ...FILTER_SUMMARY_ITEM_SX,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 210 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: pageColors.customerSales,
                        }}
                      >
                        Average Sales Window
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {averageStart} to {averageEnd}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {isApplyingFilters ? (
                  <Typography variant="caption" color="text.secondary">
                    Updating filtered results...
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            {loading && (
              <Paper elevation={0} sx={{ ...SURFACE_SX, p: 0, overflow: 'hidden' }}>
                <LinearProgress />
              </Paper>
            )}

            {!loading && !hasLoadedData && (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                No customer sales data loaded yet. Use the filters above and click &quot;Load
                Data&quot;.
              </Alert>
            )}

            {!loading && hasLoadedData && !hasFilteredResults && (
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                No customers match the current filter.
              </Alert>
            )}

            <CustomerSalesTrendsResultsTable
              rows={sortedRows}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              hasLoadedData={hasLoadedData}
            />
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
